<template>
  <div>
    <!-- 相机图标按钮 -->
    <v-btn
      icon
      :loading="searchLoading"
      @click="showUploadDialog = true"
    >
      <v-icon color="primary">
        camera_alt
      </v-icon>
    </v-btn>

    <!-- 图片上传对话框 -->
    <v-dialog
      v-model="showUploadDialog"
      max-width="500"
      persistent
    >
      <v-card>
        <v-layout class="ma-0">
          <span class="title pa-3">以圖識物</span>
          <v-spacer />
          <div>
            <v-btn
              icon
              @click="closeDialog"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </div>
        </v-layout>

        <v-card-text>
          <!-- 上传区域 -->
          <div
            ref="dropZone"
            class="upload-area"
            :class="{ 'drag-over': isDragOver }"
            @drop.prevent="onDrop"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
          >
            <div
              v-if="!uploadedImage"
              class="upload-placeholder"
            >
              <v-icon
                size="48"
                color="grey lighten-1"
              >
                image
              </v-icon>
              <div class="mt-2 subtitle-1">
                上傳商品圖片
              </div>
              <div class="caption grey--text mt-2">
                點擊選擇、拖拽文件或按 Ctrl+V 粘貼圖片
              </div>
              <v-btn
                color="primary"
                class="mt-3"
                @click="selectImage"
              >
                選擇圖片
              </v-btn>
            </div>

            <div
              v-else
              class="uploaded-image-container"
            >
              <img
                :src="uploadedImage"
                alt="已上傳圖片"
                class="uploaded-image"
              >

              <v-icon
                size="28"
                class="remove-image-btn"
                @click.stop="removeImage"
              >
                close
              </v-icon>
            </div>
          </div>

          <!-- 加载状态 -->
          <div
            v-if="searchLoading"
            class="text-center py-4"
          >
            <v-progress-circular
              indeterminate
              color="primary"
            />
            <div class="mt-2">
              正在識別圖片中的商品...
            </div>
          </div>

          <!-- 隐藏的文件输入 -->
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            style="display: none;"
            @change="onFileSelected"
          >
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Compressor from 'compressorjs'

export default {
  name: 'ImageSearchButton',
  props: {
    parcel: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      showUploadDialog: false,
      uploadedImage: '',
      imageBase64: '',
      searchLoading: false,
      isDragOver: false
    }
  },
  mounted () {
    // 监听全局粘贴事件
    document.addEventListener('paste', this.onPaste)
  },
  beforeDestroy () {
    // 移除全局粘贴事件监听
    document.removeEventListener('paste', this.onPaste)
  },
  methods: {
    selectImage () {
      this.$refs.fileInput.click()
    },

    // 处理点击选择文件
    onFileSelected (event) {
      const file = event.target.files[0]
      if (file) {
        this.processImageFile(file)
      }
    },

    // 处理拖拽上传
    onDrop (event) {
      this.isDragOver = false
      const files = event.dataTransfer.files
      if (files.length > 0) {
        const file = files[0]
        if (file.type.startsWith('image/')) {
          this.processImageFile(file)
        } else {
          this.$snackbar('請選擇圖片文件', { color: 'error' })
        }
      }
    },

    onDragOver () {
      this.isDragOver = true
    },

    onDragLeave () {
      this.isDragOver = false
    },

    // 处理粘贴上传
    onPaste (event) {
      // 只在对话框打开时处理粘贴事件
      if (!this.showUploadDialog) return

      const items = event.clipboardData?.items
      if (items) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i]
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile()
            if (file) {
              this.processImageFile(file)
              event.preventDefault()
              break
            }
          }
        }
      }
    },

    // 统一处理图片文件
    processImageFile (file) {
      // 检查文件类型
      if (!file.type.startsWith('image/')) {
        this.$snackbar('請選擇圖片文件', { color: 'error' })
        return
      }

      // 检查文件大小 (限制10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.$snackbar('圖片文件過大，請選擇小於 10MB 的圖片', { color: 'error' })
        return
      }

      // 使用 Compressor 压缩图片
      // eslint-disable-next-line no-new
      new Compressor(file, {
        quality: 0.8,
        maxWidth: 300,
        maxHeight: 300,
        minWidth: 100,
        minHeight: 100,
        success: (compressedFile) => {
          const reader = new FileReader()
          reader.onload = (e) => {
            this.uploadedImage = e.target.result
            // 去除数据URL的前缀，只保留base64字符串
            this.imageBase64 = e.target.result.split(',')[1]
            // 自动开始识别
            this.searchByImage()
          }
          reader.readAsDataURL(compressedFile)
        },
        error: (err) => {
          console.error('图片压缩失败:', err)
          this.$snackbar('圖片壓縮失敗，請重試', { color: 'error' })
        }
      })
    },

    // 移除图片
    removeImage () {
      this.uploadedImage = ''
      this.imageBase64 = ''
      this.$refs.fileInput.value = ''
    },

    // 搜索图片 - 简化为只触发事件
    async searchByImage () {
      if (!this.imageBase64) {
        this.$snackbar('請先選擇圖片', { color: 'error' })
        return
      }

      try {
        this.searchLoading = true

        // 觸發搜索事件，讓父組件使用統一的搜索服務處理
        this.$emit('search-image', this.imageBase64)

        // 關閉對話框
        this.closeDialog()
      } catch (error) {
        console.error('圖片處理錯誤:', error)
        this.$emit('search-error', '圖片處理失敗，請重試')
      } finally {
        this.searchLoading = false
      }
    },

    // 关闭对话框
    closeDialog () {
      this.showUploadDialog = false
      this.removeImage()
      this.isDragOver = false
    }
  }
}
</script>

<style lang="postcss" scoped>
.upload-area {
  border: 2px dashed #ccc;
  border-radius: 8px;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover, &.drag-over {
    border-color: #2196F3;
    background-color: #f5f5f5;
  }
}

.upload-placeholder {
  text-align: center;
  padding: 20px;
}

.uploaded-image-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
}

.uploaded-image {
  max-width: 100%;
  max-height: 180px;
  border-radius: 4px;
  object-fit: contain;
}

.remove-image-btn {
  position: absolute;
  top: 5px;
  right: 5px;
}

/* 深色模式支持 */
.theme--dark .upload-area {
  border-color: #555;

  &:hover, &.drag-over {
    border-color: #2196F3;
    background-color: #303030;
  }
}
</style>
